import { FaShieldAlt, FaLock, FaDatabase, FaUserShield, FaClock, FaCloudUploadAlt } from 'react-icons/fa'

const SecurityPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      {/* Hero Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="bg-primary-100 p-4 rounded-full">
                <FaShieldAlt className="text-primary-600 text-5xl" />
              </div>
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Your Data. Your Trust. Our Priority.
            </h1>
            <p className="text-xl text-gray-600">
              Enterprise-grade security protecting your wellness practice and client data 24/7
            </p>
          </div>
        </div>
      </section>

      {/* Security Features Grid */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Encryption */}
            <div className="text-center p-8 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex justify-center mb-4">
                <div className="bg-primary-100 p-3 rounded-full">
                  <FaLock className="text-primary-600 text-3xl" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">End-to-End Encryption</h3>
              <p className="text-gray-600">
                All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption,
                the same standard used by financial institutions and governments.
              </p>
            </div>

            {/* Access Control */}
            <div className="text-center p-8 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex justify-center mb-4">
                <div className="bg-primary-100 p-3 rounded-full">
                  <FaUserShield className="text-primary-600 text-3xl" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Role-Based Access Control</h3>
              <p className="text-gray-600">
                Granular permissions ensure staff members only access information necessary for their role.
                Full audit trails track every action for complete accountability.
              </p>
            </div>

            {/* HIPAA Compliance */}
            <div className="text-center p-8 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex justify-center mb-4">
                <div className="bg-primary-100 p-3 rounded-full">
                  <FaShieldAlt className="text-primary-600 text-3xl" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">HIPAA Compliant</h3>
              <p className="text-gray-600">
                Built from the ground up with HIPAA regulations in mind. We sign Business Associate
                Agreements (BAA) with all customers to ensure regulatory compliance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Automated Backup System */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="flex justify-center mb-4">
                <div className="bg-primary-100 p-3 rounded-full">
                  <FaDatabase className="text-primary-600 text-4xl" />
                </div>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Hybrid Backup System: Full + Continuous
              </h2>
              <p className="text-xl text-gray-600">
                Industry-leading hybrid approach combining scheduled full backups with continuous
                Write-Ahead Log (WAL) archiving for near-zero data loss protection.
              </p>
            </div>

            {/* Backup Schedule */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-primary-600 text-white py-4 px-6">
                <h3 className="text-xl font-bold flex items-center">
                  <FaClock className="mr-3" />
                  Strategic Backup Schedule
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-6">
                  {/* 6 AM Backup */}
                  <div className="flex items-start space-x-4 pb-6 border-b border-gray-200">
                    <div className="flex-shrink-0 w-24 text-center">
                      <div className="bg-primary-100 text-primary-700 font-bold text-lg py-2 px-4 rounded-lg">
                        6:00 AM
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 mb-2">Pre-Morning Sessions</h4>
                      <p className="text-gray-600">
                        Captures overnight data and client updates before early practitioners begin their day.
                        Ensures all evening notes and schedule changes are safely backed up.
                      </p>
                    </div>
                  </div>

                  {/* 1 PM Backup */}
                  <div className="flex items-start space-x-4 pb-6 border-b border-gray-200">
                    <div className="flex-shrink-0 w-24 text-center">
                      <div className="bg-primary-100 text-primary-700 font-bold text-lg py-2 px-4 rounded-lg">
                        1:00 PM
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 mb-2">Midday Checkpoint</h4>
                      <p className="text-gray-600">
                        Scheduled between morning and afternoon sessions during the typical lunch break.
                        Protects morning session notes and client data without impacting active work.
                      </p>
                    </div>
                  </div>

                  {/* 9 PM Backup */}
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-24 text-center">
                      <div className="bg-primary-100 text-primary-700 font-bold text-lg py-2 px-4 rounded-lg">
                        9:00 PM
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 mb-2">End-of-Day Capture</h4>
                      <p className="text-gray-600">
                        Final backup after evening sessions conclude. Captures the complete day's client
                        interactions, appointments, and billing information.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Continuous WAL Archiving */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg p-6 border border-green-200">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="bg-white p-3 rounded-full shadow-md">
                          <FaDatabase className="text-green-600 text-2xl" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                          Continuous WAL Archiving
                          <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ACTIVE
                          </span>
                        </h4>
                        <p className="text-gray-700 mb-3">
                          In addition to scheduled full backups, our system continuously archives database
                          transaction logs (WAL files) every 60 seconds or whenever 16MB of data is written.
                          This provides:
                        </p>
                        <ul className="text-gray-700 space-y-2">
                          <li className="flex items-start">
                            <span className="text-green-600 font-bold mr-2">✓</span>
                            <span><strong>Point-in-time recovery</strong> to within 1 minute of any incident</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-green-600 font-bold mr-2">✓</span>
                            <span><strong>Near-zero data loss</strong> (RPO &lt; 1 minute vs. 5-7 hours)</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-green-600 font-bold mr-2">✓</span>
                            <span><strong>Granular restore options</strong> - recover to any specific moment in time</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Backup Stats */}
                <div className="mt-6 grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary-600">3x + WAL</div>
                    <div className="text-sm text-gray-600 mt-1">Hybrid Backups</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary-600">30</div>
                    <div className="text-sm text-gray-600 mt-1">Days Retention</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">&lt;1min</div>
                    <div className="text-sm text-gray-600 mt-1">Max Data Loss (RPO)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cloud Redundancy */}
            <div className="mt-8 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-6 border border-primary-200">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="bg-white p-3 rounded-full shadow-md">
                    <FaCloudUploadAlt className="text-primary-600 text-2xl" />
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Dual-Location Storage</h4>
                  <p className="text-gray-700">
                    Every backup is stored in two locations: on secure local servers and automatically
                    uploaded to encrypted cloud storage (Digital Ocean Spaces). This dual-redundancy
                    ensures your data is protected even in the event of catastrophic hardware failure.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Security Measures */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Additional Security Measures
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-3">🔐 Two-Factor Authentication</h3>
                <p className="text-gray-600">
                  Optional 2FA adds an extra layer of security to protect your account from unauthorized access.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-3">📊 Activity Monitoring</h3>
                <p className="text-gray-600">
                  Real-time monitoring and alerts for suspicious activities or unauthorized access attempts.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-3">🔄 Automatic Updates</h3>
                <p className="text-gray-600">
                  Security patches and updates are applied automatically to protect against emerging threats.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-3">🛡️ DDoS Protection</h3>
                <p className="text-gray-600">
                  Enterprise-grade DDoS mitigation ensures your practice remains accessible at all times.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-3">🔍 Regular Security Audits</h3>
                <p className="text-gray-600">
                  Third-party security audits and penetration testing ensure our defenses stay strong.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-3">📝 Detailed Audit Logs</h3>
                <p className="text-gray-600">
                  Complete audit trails of all system activities for compliance and accountability.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Certifications */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Compliance & Certifications
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-xl text-gray-900 mb-2">HIPAA</h3>
                <p className="text-gray-600">
                  Full HIPAA compliance with signed Business Associate Agreements
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-xl text-gray-900 mb-2">GDPR</h3>
                <p className="text-gray-600">
                  European data protection regulation compliance
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-xl text-gray-900 mb-2">SOC 2</h3>
                <p className="text-gray-600">
                  Industry-standard security and availability controls
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-4">
            Experience Enterprise-Grade Security
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join hundreds of wellness practitioners who trust Acuity Practice with their most sensitive data
          </p>
          <div className="flex justify-center space-x-4">
            <a
              href="https://acuitypractice.com/login"
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Start Free Trial
            </a>
            <a
              href="/contact"
              className="bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-800 transition-colors border-2 border-white"
            >
              Contact Security Team
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default SecurityPage
